import _ from 'lodash';
import { Contacts } from '@or-sdk/hitl';
import { ContactResponseDto, FieldSchemaResponseDto } from '@or-sdk/contacts';

import { HttpSDK, HITL } from '@/api';
import { Contact, ContactProperties, ContactBookSettingsSchemaProperty } from '@/types';
import { AppRouter } from '@/common';
import {
  useContactsStore,
  useContactsGetters,
  useContactsSetters,
} from './use-contacts-store';
import { SchemaItem } from './types';
import { UIEventBus, THROW_API_ERROR } from '@/common/ui-event-bus';

export const getEmptyContact = () => ({
  contactId: '',
  phoneNumbers: [] as string[],
  firstName: '',
  lastName: '',
  name: '',
  photo: '',
  emails: [] as string[],
  visibilityTags: [] as string[],
  properties: {},
});

/** In HitL apps we extract common contact properties from response object  */
export const modifyContactResponseObject = (contactDto: ContactResponseDto): Contact => {
  const contact = getEmptyContact();
  const keyValue = _.mapValues(_.keyBy(contactDto.fieldValues, 'machine_name'), 'value');

  contact.contactId = contactDto.id;
  contact.properties = contactDto.fieldValues;

  if (keyValue.phone_numbers) {
    contact.phoneNumbers = (keyValue.phone_numbers as any[]).map(({ val }) => val);
  }
  if (keyValue.first_name) {
    contact.firstName = keyValue.first_name as string;
  }
  if (keyValue.last_name) {
    contact.lastName = keyValue.last_name as string;
  }
  if (keyValue.name) {
    contact.name = keyValue.name as string;
  }
  if (keyValue.photo) {
    contact.photo = keyValue.photo as string;
  }
  if (keyValue.emails) {
    contact.emails = (keyValue.emails as any[]).map(({ val }) => val);
  }
  if (keyValue.visibility_tags) {
    contact.visibilityTags = keyValue.visibility_tags as string[];
  }

  return contact;
};

export const useContactsActions = () => {
  const store = useContactsStore();
  const { activeContactId } = useContactsGetters();
  const {
    addOrUpdateContactsInStore,
    addOrMergeContactInStore,
    deleteContactFromStore,
    setSchema,
    setIsLoadingByContactIds,
    updateContactEventTimestamp,
  } = useContactsSetters();

  const setSearchText = (search: string) => {
    store.searchText = search;
  };

  const setActiveContactById = async (options?: {
    contactId?: string;
    reset?: boolean;
  }) => {
    const { contactId = '', reset = false } = options || {};

    if (contactId && reset) {
      deleteContactFromStore(contactId);
      await getAPIContactById(contactId);
    }

    /* Reset active session when contact changes */
    if (contactId !== activeContactId.value) {
      AppRouter.appendQuery({ query: { contactId, sessionId: '' } });
    }
  };

  const reInitializeActiveContact = () => {
    setActiveContactById({ contactId: activeContactId.value, reset: true });
  };

  /**
   * Receive updates for the contacts we have interacted with.
   * TODO: Split into "Create / Update" event handlers.
   * TODO: Are we interested in "Create" events?
   */
  const onWSUpsertContact = async (args: {
    contact: Partial<Contact>;
    request?: boolean;
  }) => {
    const { contact, request } = args;
    const { contactId } = contact as Contact;

    /* Merge updates into existing contacts */
    if (store.contactsById[contactId]) {
      return addOrMergeContactInStore(contact);
    }

    /* Get complete contact using API when explicitly requested */
    if (request) {
      await getAPIContactById(contactId);
    }
  };

  /* TODO: We can delete sessions and events as well */
  const onWSDeleteContactById = (contactId: string) => {
    if (contactId === activeContactId.value) {
      setActiveContactById();
    }
    deleteContactFromStore(contactId);
  };

  const getAPISchema = async () => {
    try {
      const { items } = await HttpSDK.contacts.fieldSchemaApi.listSchemas({});
      const schema = createSchema({
        fields: items,
        extraPropertyData: store.contactBookInfo.schema,
      });
      setSchema({ schema });
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
    }
  };

  const createSchema = (args: {
    fields: FieldSchemaResponseDto[];
    extraPropertyData: { [property: string]: ContactBookSettingsSchemaProperty; };
  }): SchemaItem[] => {
    const records: SchemaItem[] = [];

    for (const field of args.fields) {
      const { machine_name: machineName } = field;
      const schemaItemField: any = {
        id: field.id,
        key: machineName,
        singularLabel: field.singular_label,
        label: field.label,
        type: field.type,
        visibility: false,
        order: 0,
      };

      if (field.properties) {
        schemaItemField.properties = field.properties;
      }

      if (args.extraPropertyData[machineName]) {
        Object.assign(schemaItemField, args.extraPropertyData[machineName]);
      }

      records.push(schemaItemField);
    }

    return records;
  };

  const getAPIContactById = async (contactId: string) => {
    setIsLoadingByContactIds({ contactIds: [contactId], isLoading: true });

    try {
      const [contact, meta] = await Promise.all([
        HttpSDK.contacts.contactApi.getContact(contactId),
        HITL.contactsMeta.searchContactsMeta({ contactIds: [contactId] }),
      ]);

      if (!contact) {
        return;
      }

      if (meta.data[contactId]) {
        updateContactEventTimestamp({
          contactId,
          timestamp: meta.data[contactId].eventTimestamp,
        });
      }

      const mContact = modifyContactResponseObject(contact);
      addOrUpdateContactsInStore(mContact);
      return mContact;
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
      throw error;
    } finally {
      setIsLoadingByContactIds({ contactIds: [contactId], isLoading: false });
    }
  };

  const getAPIContacts = async (args: {
    contactIds: string[];
    reset?: boolean;
  }) => {
    /* Do not re-request contacts that are already in store unless explicitly requested */
    const contactIds = _.uniq(args.contactIds).filter(contactId => {
      return args.reset || !store.contactsById[contactId];
    });

    if (!contactIds.length) {
      return;
    }

    try {
      setIsLoadingByContactIds({ contactIds, isLoading: true });
      const [contacts, meta] = await Promise.all([
        HttpSDK.contacts.contactApi.findContacts({
          contactIds,
        }),
        HITL.contactsMeta.searchContactsMeta({ contactIds }),
      ]);

      for (const item of Object.values(meta.data)) {
        updateContactEventTimestamp({
          contactId: item.contactId,
          timestamp: item.eventTimestamp,
        });
      }

      const mContacts = contacts.map(modifyContactResponseObject);
      addOrUpdateContactsInStore(mContacts);
      return mContacts;
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
      throw error;
    } finally {
      setIsLoadingByContactIds({ contactIds, isLoading: false });
    }
  };

  const getAPIContactsChunk = async (options: Contacts.SearchContactsOptions) => {
    /** TODO: Return response type */
    const response = await HITL.contacts.searchContacts({
      contact_book: store.contactBookInfo.id,
      ...options,
    });

    /* We store contacts in store anyways */
    const mContacts = response.items.map(modifyContactResponseObject);
    addOrUpdateContactsInStore(mContacts);

    return {
      contacts: mContacts,
      offset: response.offset,
      total: response.total,
    };
  };

  const searchContactsByTerm = async (searchTerm: string) => {
    try {
      const contacts = await HttpSDK.contacts.contactApi.findContacts({
        contact_book: store.contactBookInfo.id,
        q: searchTerm,
      });

      return contacts.map(modifyContactResponseObject);
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
      throw error;
    }
  };

  /** Merge source contact into target contact */
  const mergeAPIContacts = async (args: {
    sourceId: string;
    targetId: string;
  }) => {
    try {
      return await HttpSDK.contacts.contactApi.mergeContacts(args.targetId, {
        contact_book: store.contactBookInfo.id,
        mergeContactId: args.sourceId,
      });
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
      throw error;
    }
  };

  /**
   * TODO: Might now work properly?
   * Will require a rework of the flow card?
   * Available as a feature in cards?
   */
  const createAPIContact = async (contact: Partial<Contact>) => {
    try {
      /** TODO: Maybe we can re-use return result? */
      const { id } = await HttpSDK.contacts.contactApi.createContact({
        contact_book: store.contactBookInfo.id,
        data: Object.values(contact.properties || {}),
      });
      return await getAPIContactById(id);
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
      throw error;
    }
  };

  const updateAPIContact = async (args: {
    contactId: string;
    properties: ContactProperties;
  }) => {
    try {
      const { contactId, properties } = args;
      const response = await HttpSDK.contacts.contactApi.updateContact(contactId, {
        contact_book: store.contactBookInfo.id,
        data: Object.values(properties),
      });
      addOrUpdateContactsInStore(modifyContactResponseObject(response));
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
      throw error;
    }
  };

  const deleteAPIContactById = async (contactId: string) => {
    try {
      await HttpSDK.contacts.contactApi.deleteContact(contactId, {
        contact_book: store.contactBookInfo.id,
      });
      onWSDeleteContactById(contactId);
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
    }
  };

  return {
    setSearchText,
    setActiveContactById,
    reInitializeActiveContact,
    onWSUpsertContact,
    onWSDeleteContactById,
    getAPISchema,
    getAPIContactById,
    getAPIContactsChunk,
    getAPIContacts,
    createAPIContact,
    searchContactsByTerm,
    mergeAPIContacts,
    updateAPIContact,
    deleteAPIContactById,
    modifyContactResponseObject,
  };
};
